import { useEffect, useRef } from "react";
import { hooks } from "front-chat-sdk";
import {
  checkIsSupportChatVisible,
  setFrontChatFaded,
  FADE_DURATION_MS,
  SCROLL_THRESHOLD_PX,
} from "./helpers";

const { useFrontChatBoot } = hooks;

const useSupportChat = (
  locationPathname: string,
  routes?: string[],
  chatBotId?: string
) => {
  const isSupportChatVisible = routes
    ? checkIsSupportChatVisible(routes, locationPathname)
    : true;
  const { frontChat, initialize } = useFrontChatBoot();
  const lastScrollYRef = useRef(0);
  const isHiddenByScrollRef = useRef(false);
  const hideTimerRef = useRef<number | undefined>(undefined);

  const clearHideTimer = () => {
    if (hideTimerRef.current) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = undefined;
    }
  };

  const hideWithFade = () => {
    if (isHiddenByScrollRef.current) {
      return;
    }

    setFrontChatFaded(true);
    clearHideTimer();

    hideTimerRef.current = window.setTimeout(() => {
      isHiddenByScrollRef.current = true;
      hideTimerRef.current = undefined;
    }, FADE_DURATION_MS);
  };

  const showWithFade = () => {
    clearHideTimer();
    setFrontChatFaded(false);
    isHiddenByScrollRef.current = false;
  };

  useEffect(() => {
    if (!isSupportChatVisible) {
      clearHideTimer();
      setFrontChatFaded(false);

      if (frontChat) {
        frontChat("hide");
        frontChat("shutdown");
      }

      isHiddenByScrollRef.current = false;

      return;
    }

    if (!initialize) {
      return;
    }

    initialize({
      chatId: chatBotId,
      useDefaultLauncher: true,
    });
  }, [frontChat, initialize, isSupportChatVisible, locationPathname]);

  useEffect(() => {
    if (!isSupportChatVisible || !frontChat) {
      return;
    }

    const handleScroll = () => {
      const currentY = window.scrollY;
      const isScrollingDown = currentY > lastScrollYRef.current;
      const isScrollingUp = currentY < lastScrollYRef.current;
      const scrollHeight = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight
      );
      const remainingPx = scrollHeight - (currentY + window.innerHeight);

      if (remainingPx <= SCROLL_THRESHOLD_PX && isScrollingDown) {
        hideWithFade();
      } else if (remainingPx > SCROLL_THRESHOLD_PX && isScrollingUp) {
        showWithFade();
      }

      lastScrollYRef.current = currentY;
    };

    lastScrollYRef.current = window.scrollY;

    document.body.addEventListener("scroll", handleScroll);
    window.addEventListener("scroll", handleScroll);

    return () => {
      clearHideTimer();
      document.body.removeEventListener("scroll", handleScroll);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [frontChat, isSupportChatVisible]);
};

export default useSupportChat;
